// src/hooks/useSessionSync.ts
import { useEffect } from 'react';

// Define the shape of the message we expect
interface AuthMessage {
  type: 'AUTH_STATE_CHANGE';
  userId: string | null; // or number, depending on your user ID type
}

export const useSessionSync = (currentUserId: string | null, logout: () => void) => {
  useEffect(() => {
    // 1. Create the channel. Name it uniquely for your app.
    const channel = new BroadcastChannel('waivera_auth_channel');

    // 2. Define the listener
    channel.onmessage = (event: MessageEvent<AuthMessage>) => {
      const { type, userId: incomingUserId } = event.data;

      if (type === 'AUTH_STATE_CHANGE') {
        // 1. If the incoming message is a logout (null), every tab must log out.
        if (incomingUserId === null && currentUserId !== null) {
          console.warn('Logout detected from another tab.');
          window.location.reload();
          return;
        }

        // 2. If someone logs in (incoming is NOT null) 
        // AND we already have a user (current is NOT null)
        // AND they are different people... then we have a mismatch.
        if (incomingUserId !== null && currentUserId !== null && incomingUserId !== currentUserId) {
          console.warn('Session mismatch: Different user logged in elsewhere.');
          window.location.reload();
          return;
        }
      }
    };

    // 3. Cleanup: Close channel when component unmounts
    return () => {
      channel.close();
    };
  }, [currentUserId, logout]);

  // Helper function to broadcast changes
  const broadcastAuthChange = (newUserId: string | null) => {
    const channel = new BroadcastChannel('waivera_auth_channel');
    channel.postMessage({
      type: 'AUTH_STATE_CHANGE',
      userId: newUserId,
    });
    channel.close(); // Close immediately after sending since we only opened it to send
  };

  return { broadcastAuthChange };
};